import { Form, Tooltip, Typography } from "antd";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import { HelpIcon } from "components/Icons";
import Vocabulary from "components/Vocabulary";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import {
  FeatureFlagContentValuesType,
  useFeatureFlag,
} from "contexts/FeatureFlagContext";
import _, { isEmpty } from "lodash";
import React, {
  ChangeEvent,
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import {
  FieldTypes,
  checkCharLimit,
  generateFormFields,
} from "utils/formUtils";
import { SEVERITY_NAMES } from "views/Findings/Constants";

import {
  CONFIG_UPDATE_WARNING_MESSAGE,
  CUSTOM_FILED_SUPPORTED_INTEGRATIONS,
  GenericIntegrationTicketDefaultValues,
  genericIntegrationTicketProductSubproductAppendAll,
} from "../genericIntegrationTicketConstants";
import {
  RenderCustomFields,
  ResolvedFindingStatusNames,
  generateResponse,
  getFreshServiceDefaultData,
  integrationTicketingToolsProductOptionsMutator,
  useUnifiedMultiTemplateOptions,
  useUnifiedSingleTemplateOptions,
} from "../genericIntegrationTicketUtils";

const { Link } = Typography;

type FreshServiceMappinFormProps = {
  loginConfigId: number;
  onUpdateConfigComponent: (value: boolean) => void;
  config: GenericIntegrationTicketDefaultValues;
  url: string;
  name: string;
};

const FreshServiceMappinForm = (
  {
    loginConfigId,
    onUpdateConfigComponent,
    url,
    name,
    ...props
  }: FreshServiceMappinFormProps,
  ref: ForwardedRef<any>,
) => {
  const [initialData] = useState(() =>
    getFreshServiceDefaultData(props.config),
  );
  const isEdit = Boolean(props.config?.id);
  const [form] = Form.useForm();
  const [data, setData] = useState(initialData);
  const [transitionStatusOptions, setTransitionStatusOptions] = useState([]);
  const [isResolvedStatusDisabled, setIsResolvedStatusDisabled] = useState(
    !data.markResolved,
  );
  const [markReopen, setMarkReopen] = useState(data.markReopen);
  const [priorities, setPriorities] = useState([]);
  const featureFlags = useFeatureFlag();
  const toolName = name.toUpperCase();
  const { getUnifiedSingleTemplates, unifiedSingleDefaultTemplate } =
    useUnifiedSingleTemplateOptions();
  const { getUnifiedMultiTemplates, unifiedMultiDefaultTemplate } =
    useUnifiedMultiTemplateOptions();
  const filteredIntegrations = useMemo(
    () =>
      _.compact(
        _.map(CUSTOM_FILED_SUPPORTED_INTEGRATIONS, (entry) => {
          const featureFlag = (entry as any).featureFlag;
          if (
            (featureFlag &&
              featureFlags[
                featureFlag as keyof FeatureFlagContentValuesType
              ]) ||
            !featureFlag
          ) {
            return entry.tool;
          }
          return null;
        }),
      ),
    [featureFlags],
  );

  const thisRef: MutableRefObject<any> = useRef();
  const freshServiceIssueTypesRef: MutableRefObject<any> = useRef(
    props.config?.projectKey === "Service" &&
      props.config?.properties &&
      props.config?.issueType &&
      props.config?.properties[props.config?.issueType]
      ? [
          {
            name: props.config.issueType,
            id: props.config.properties[props.config.issueType],
          },
        ]
      : [],
  );

  useEffect(() => {
    let cancelTransionStatusApi = _.noop;
    const getTransitionStatusOptions = async () => {
      if (!loginConfigId || !data.issueType || !data.projectKey) {
        return false;
      }
      const requestPayload = {
        loginConfigId,
        issueType: data.issueType,
        projectKey: data.projectKey,
      };
      const {
        promise: transitionStatusPromise,
        cancel: transitionStatusReject,
      } =
        ToolsIntegrationsAPI.getJiraCloseTransitionStatusOptions(
          requestPayload,
        );
      cancelTransionStatusApi = transitionStatusReject;
      const statusOptions = await transitionStatusPromise;
      cancelTransionStatusApi = _.noop;
      setTransitionStatusOptions(statusOptions);
    };

    getTransitionStatusOptions();
    return cancelTransionStatusApi;
  }, [loginConfigId, data.issueType, data.projectKey]);

  const getFreshServiceIssutTypes = useCallback(
    (params) => {
      if (data.projectKey !== "Service") {
        return { promise: Promise.resolve([]), cancel: _.noop };
      }
      const { promise, cancel } =
        ToolsIntegrationsAPI.getFreshServiceIssueTypes(
          { projectKey: data.projectKey, loginConfigId },
          params,
        );
      promise.then((res) => {
        //add searched issue type which does not exist on the same page
        if (!isEmpty(freshServiceIssueTypesRef.current)) {
          freshServiceIssueTypesRef.current = _.uniqBy(
            [...res, ...freshServiceIssueTypesRef.current],
            "id",
          );
        } else {
          freshServiceIssueTypesRef.current = res;
        }
      });

      return { promise, cancel };
    },
    [data.projectKey, loginConfigId],
  );

  const onOk = async () => {
    await form.validateFields();
    const data = { ...form.getFieldsValue(), ticketSystemType: "FRESHSERVICE" };
    data.ticketClosureStatus = _.isEmpty(data.ticketClosureStatus)
      ? null
      : data.ticketClosureStatus;
    data.customFields = [];

    const configValues: any = await generateResponse(
      data,
      featureFlags,
      toolName,
      props.config?.id,
      loginConfigId,
      url,
      thisRef.current?.getCustomFields(),
    );

    const issueTypeInPayload = freshServiceIssueTypesRef.current?.find(
      (_issueType: any) => _issueType.name === data.issueType,
    );
    configValues.configValues.properties[data.issueType] =
      issueTypeInPayload?.id;

    if (!_.isPlainObject(configValues)) {
      return Promise.reject("Mapping already exist");
    }
    await ToolsIntegrationsAPI[
      !isEdit ? "integrateTicketSystemTool" : "updateTicketSystemToolMapping"
    ](configValues.configValues, props.config?.id).promise;
    message.success(`FreshService Mapping saved`);
    setData({});
    onUpdateConfigComponent && onUpdateConfigComponent(false);
  };

  useEffect(() => {
    if (!data.projectKey || !data.issueType) {
      return;
    }
    if (data.projectKey) {
      setPriorities([]);
      const { cancel, promise } =
        ToolsIntegrationsAPI.getTicketSystemToolPriorities({
          projectKey: data.projectKey,
          issueType: data.issueType,
          loginConfigId,
        });
      promise.then((response) => {
        if (!(isEdit && data.projectKey === initialData.projectKey)) {
          const severities: any = {};
          const severityNames = Object.keys(SEVERITY_NAMES);
          response.forEach((entry: any, index: number) => {
            severities[severityNames[index]] = entry.id;
          });

          if (severityNames.length > response.length) {
            for (
              let index = response.length;
              index < severityNames.length;
              index++
            ) {
              severities[severityNames[index]] =
                severities[severityNames[index - 1]];
            }
          }
          form.setFieldsValue(severities);
        }
        setPriorities(response);
      });
      return cancel;
    }
  }, [
    isEdit,
    data.projectKey,
    data.issueType,
    loginConfigId,
    form,
    initialData.projectKey,
  ]);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        ticketUnifiedSingleTemplateId: unifiedSingleDefaultTemplate,
        ticketUnifiedMultipleTemplateId: unifiedMultiDefaultTemplate,
      });
    }
  }, [isEdit, form, unifiedSingleDefaultTemplate, unifiedMultiDefaultTemplate]);

  useEffect(() => {
    if (data.projectKey === "Incident") {
      form.setFieldsValue({ issueType: "Incident" });
    }
  }, [data.projectKey, form]);

  const resetSubProductsField = useCallback(
    (values) => {
      setData((data: GenericIntegrationTicketDefaultValues) => ({
        ...data,
        product: values,
      }));
      form.setFieldsValue({ subProduct: ["all"] });
    },
    [form],
  );

  const commonSeverityFreshServiceProps = useMemo(
    () => ({
      colSpan: 5,
      className: "m-b-0",
      type: FieldTypes.SELECT,
      style: { flex: "0 0 20%" },
      options: priorities,
      loading: data.projectKey && _.isEmpty(priorities),
      required: true,
    }),
    [priorities, data.projectKey],
  );

  const modalProps = {
    title: `${isEdit ? "Edit" : "Add"} FreshService Mapping`,
    width: MODAL_SIZE_LARGE,
    onOk,
    disabled: !hasPermission(
      isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS,
    ),
  };

  // if more than one product or All product is selected then subproduct should be disabled
  // this would work as previous, just will get the product id from array, as product is multi select
  const productId =
    data.product && data.product[0] !== "all" ? data.product[0] : null;

  const onCreateSubproduct = useCallback(
    (createdSubproduct) => {
      const subProductValues = form.getFieldValue("subProduct");
      form.setFieldsValue({
        subProduct: [...subProductValues, createdSubproduct.id],
      });
      setData((prevdata) => ({
        ...prevdata,
        subProduct: [...subProductValues, createdSubproduct.id],
      }));
    },
    [form],
  );

  const onCreateProduct = useCallback(
    (createdProduct) => {
      const productsAfterCreate = [
        ...form.getFieldValue("product"),
        createdProduct.id,
      ];
      form.setFieldsValue({
        product: productsAfterCreate,
        subProduct: ["all"],
      });
      setData((data) => ({
        ...data,
        product: productsAfterCreate,
        subProduct: ["all"],
      }));
    },
    [form],
  );

  const productSubProductFields = useMemo(
    () => [
      {
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        // disabled:isEdit,
        props: {
          dropdownType: "product",
          showAddButton: true,
          optionsMutator: (
            options: [{ label: string; value: number | string }],
          ) =>
            integrationTicketingToolsProductOptionsMutator(
              options,
              form.getFieldValue("product"),
            ),
          onChange: resetSubProductsField,
          onCreate: onCreateProduct,
          mode: "multiple",
        },
      },
      {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: data.product && data.product.length > 1,
        props: {
          productId: productId,
          showAddButton: Boolean(productId),
          dropdownType: "subProduct",
          onCreate: onCreateSubproduct,
          onChange: (values: any[]) => {
            if (values.includes("all")) {
              form.setFieldsValue({ subProduct: ["all"] });
            }
          },
          optionsMutator: (options: any[]) => {
            const updatedOptions =
              genericIntegrationTicketProductSubproductAppendAll(options);
            const selectedValues = form.getFieldValue("subProduct") || [];
            const isAllSelected = selectedValues.includes("all");

            return updatedOptions.map((option) => ({
              ...option,
              disabled: isAllSelected && option.value !== "all",
            }));
          },

          mode: "multiple",
        },
      },
    ],
    [
      form,
      productId,
      resetSubProductsField,
      onCreateSubproduct,
      data.product,
      onCreateProduct,
    ],
  );

  const handleMarkResolveChecked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checked =
        !data.projectKey || !data.issueType || !event.target.checked;
      setIsResolvedStatusDisabled(checked);
    },
    [data.projectKey, data.issueType],
  );

  const handleMarkReopen = useCallback((event) => {
    const checked = event.target.checked;
    setMarkReopen(checked);
  }, []);

  const getJiraLabels = useCallback(
    (params) => {
      if (!loginConfigId) {
        return Promise.resolve([]);
      }
      return ToolsIntegrationsAPI.getJiraLabels({ loginConfigId, ...params });
    },
    [loginConfigId],
  );

  const findingField = useMemo(
    () => [
      {
        render: (props: any) => <div {...props} />,
        className: "p-y-sm",
      },
      {
        name: "markResolved",
        label: (
          <>
            Close Ticket when Finding is <Vocabulary name={"mitigated"} />
            <Tooltip
              title={
                <>
                  Automatically closes FreshService ticket when an issue is
                  fixed. The other states that may trigger ticket close are{" "}
                  {<ResolvedFindingStatusNames />} based on the global
                  configuration as provided <br />
                  <Link href="#/globalsettings?activeStep=step17">
                    Click here
                  </Link>
                </>
              }
              placement="top"
            >
              <HelpIcon />
            </Tooltip>
          </>
        ),
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        inline: true,
        props: {
          onChange: handleMarkResolveChecked,
        },
      },
      {
        name: "resolvedStatus",
        label: "Close Transition Name",
        help: `Close Transition Name should be set as the final (close) status in your defined FreshService workflow for your project. For example 'CLOSED', 'RESOLVED' or 'DONE'.`,
        type: FieldTypes.SELECT,
        options: transitionStatusOptions,
        required: !isResolvedStatusDisabled,
        disabled: isResolvedStatusDisabled,
        colSpan: 12,
      },
      {
        name: "markReopen",
        label: "Re-open ticket if finding remains unresolved from a scan",
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        inline: true,
        props: {
          onChange: handleMarkReopen,
        },
        className: "m-t-l",
      },
      {
        name: "reopenStatus",
        label: "Re-open Transition Name",
        type: FieldTypes.SELECT,
        options: transitionStatusOptions,
        disabled: !markReopen,
        required: markReopen,
        colSpan: 12,
        help: (
          <>
            FreshService status(transition) which can be directly assigned from
            closed status(transition)
          </>
        ),
        props: {
          allowClear: true,
        },
      },
      {
        name: "labels",
        label: "Tags",
        help: `These "tags" will be added to the tickets.`,
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        props: {
          mode: "tags",
          api: getJiraLabels,
        },
        rules: [
          {
            validator: (_: any, values: string[]) => {
              const regEx = /\s/;
              if (values) {
                for (let i = 0; i < values.length; i += 1) {
                  if (regEx.test(values[i])) {
                    return Promise.reject(
                      "The label can't contain blank spaces.",
                    );
                  }
                }
              }
              return Promise.resolve();
            },
          },
        ],
      },
    ],
    [
      getJiraLabels,
      handleMarkResolveChecked,
      isResolvedStatusDisabled,
      transitionStatusOptions,
      handleMarkReopen,
      markReopen,
    ],
  );

  const FreshServiceFields = useMemo(
    () => [
      ...productSubProductFields,
      {
        name: "projectKey",
        label: "Ticket Type",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        options: [
          {
            label: "Incident",
            value: "Incident",
          },
          {
            label: "Service",
            value: "Service",
          },
        ],
        props: {
          onChange: (value: string) => {
            isEdit && message.warn(CONFIG_UPDATE_WARNING_MESSAGE);
            setData((data: GenericIntegrationTicketDefaultValues) => ({
              ...data,
              projectKey: value,
              issueType: value === "Incident" ? "Incident" : "",
              issueTypeId: "",
            }));
            if (value === "Service") {
              form.setFieldsValue({ issueType: "" });
            }
          },
        },
      },
      {
        name: "issueType",
        label: "Service Item",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        disabled: data.projectKey === "Incident",
        props: {
          api: getFreshServiceIssutTypes,
          resetOptionsProp: getFreshServiceIssutTypes,
          startPage: 1,
          parseSelectOptions: (options: any[]) =>
            (options || []).map((option) => ({
              label: option.name,
              value: option.name,
            })),
          onChange: (value: string) => {
            isEdit && message.warn(CONFIG_UPDATE_WARNING_MESSAGE);
            setData((data: GenericIntegrationTicketDefaultValues) => ({
              ...data,
              issueType: value,
              issueTypeId: _.find(freshServiceIssueTypesRef.current, {
                name: value,
              })?.id,
            }));
          },
        },
      },
      ...(featureFlags.UNIFIED_TICKET_TEMPLATE
        ? [
            {
              name: "ticketUnifiedSingleTemplateId",
              label: "Single Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedSingleTemplates,
              },
            },
            {
              name: "ticketUnifiedMultipleTemplateId",
              label: "Multi-Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedMultiTemplates,
              },
            },
          ]
        : []),
      {
        label: `ArmorCode Severity -> FreshService Priority`,
        type: FieldTypes.GROUP,
        required: false,
        fields: [
          {
            name: "critical",
            label: <Vocabulary name="critical" />,
            ...commonSeverityFreshServiceProps,
          },
          {
            name: "high",
            label: <Vocabulary name="high" />,
            ...commonSeverityFreshServiceProps,
          },
          {
            name: "medium",
            label: <Vocabulary name="medium" />,
            ...commonSeverityFreshServiceProps,
          },
          {
            name: "low",
            label: <Vocabulary name="low" />,
            ...commonSeverityFreshServiceProps,
          },
          {
            name: "info",
            label: <Vocabulary name="info" />,
            ...commonSeverityFreshServiceProps,
          },
        ],
      },
      ...findingField,
      {
        name: "transitions",
        label: "Transitions",
        colSpan: 24,
        type: FieldTypes.TEXT_AREA,
        help: (
          <>
            {" "}
            Please provide the Status workflow in JSON format. For reference,
            please refer to the{" "}
            <Typography.Link
              href={"/downloads/freshServiceTransitions.json"}
              download
            >
              {" "}
              Demo{" "}
            </Typography.Link>{" "}
            File. <br />
            {
              "Note: Transition field is mandatory if there are fields which are required for closure, and the input json should be as per the transition rules given in the FreshService instance used."
            }{" "}
          </>
        ),
        NoDefaultLengthRule: true,
        rules: [
          checkCharLimit(4000),
          {
            validator: (_, value) => {
              if (!isEmpty(value)) {
                try {
                  JSON.parse(value);
                } catch (e) {
                  return Promise.reject(new Error("Invalid JSON Text"));
                }
              }
              return Promise.resolve();
            },
          },
        ],
      },
    ],
    [
      commonSeverityFreshServiceProps,
      featureFlags.UNIFIED_TICKET_TEMPLATE,
      findingField,
      getFreshServiceIssutTypes,
      getUnifiedMultiTemplates,
      getUnifiedSingleTemplates,
      productSubProductFields,
      form,
      data.projectKey,
      isEdit,
    ],
  );

  //TODO need to resolve TS
  return (
    <BaseModal
      ref={ref}
      modalProps={modalProps}
      // @ts-expect-error
      afterClose={props.afterClose}
    >
      <Form form={form} initialValues={data} layout="vertical">
        {generateFormFields(FreshServiceFields)}
      </Form>
      {filteredIntegrations.indexOf("FreshService") !== -1 ? (
        <RenderCustomFields
          // @ts-expect-error
          form={form}
          ref={thisRef}
          initialValues={data}
          loginConfigId={loginConfigId}
          changeMode={isEdit}
          configId={props.config?.id}
          toolName={toolName}
        />
      ) : null}
    </BaseModal>
  );
};

export default forwardRef(FreshServiceMappinForm);
